/* global BigInt */
// Formatting helpers for the Bonus Requests view. Money arrives from the API as exact decimal
// strings ("1790.00", "0.66840000"), so it is only grouped for display, never parsed to floats.

// Step status → label + text colour token.
const STEPS = {
  pass: { label: 'Pass', tone: 'text-positive' },
  warn: { label: 'Needs decision', tone: 'text-warning' },
  fail: { label: 'Fail', tone: 'text-negative' },
  unknown: { label: 'Cannot verify', tone: 'text-negative' },
  skipped: { label: 'Skipped', tone: 'text-ink-mute' },
};

export const stepMeta = (status) => STEPS[status] || { label: status || '—', tone: 'text-ink-mute' };

// trimDecimal drops trailing zeros: "82141.459973912220000000" → "82141.45997391222".
export const trimDecimal = (s) => (String(s).includes('.') ? String(s).replace(/0+$/, '').replace(/\.$/, '') : String(s));

// rateText: today's rate used to convert the comp, as "1 ETH = 2,500 USD"; empty when not converted.
export const rateText = (p) => (p.issue_rate_usd ? `1 ${p.issue_currency} = ${formatAmount(trimDecimal(p.issue_rate_usd))} USD` : '');

// formatAmount groups the integer part of a decimal string and appends the currency.
export const formatAmount = (amount, currency) => {
  if (amount === null || amount === undefined || amount === '') return '—';
  const [int, frac] = String(amount).split('.');
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const text = frac !== undefined ? `${grouped}.${frac}` : grouped;
  return currency ? `${text} ${currency}` : text;
};

// parseDecimal: a decimal string as its digits and scale ("1.50" → 150n, 2); null if it is not one.
const parseDecimal = (s) => {
  const m = /^(\d+)(?:\.(\d+))?$/.exec(String(s ?? '').trim());
  return m ? { digits: BigInt(m[1] + (m[2] || '')), scale: (m[2] || '').length } : null;
};

// usdValue: what amount is worth at rate (USD per unit), to the cent, half up; exact (no floats).
// '' when either is missing. The comp is rounded up in its own currency, so this is what the
// player actually gets in USD, not the rounded USD basis.
export const usdValue = (amount, rate) => {
  const a = parseDecimal(amount);
  const r = parseDecimal(rate);
  if (!a || !r) return '';
  const div = 10n ** BigInt(a.scale + r.scale);
  const cents = (a.digits * r.digits * 100n + div / 2n) / div;
  return `${cents / 100n}.${String(cents % 100n).padStart(2, '0')}`;
};

// storedAmount: an amount read back from the comp table (NUMERIC(20,8): "50.00000000") without the
// padding zeros, keeping at least 2 decimals: "50.00", "0.00146618", "0.10".
export const storedAmount = (amount) => {
  if (amount === null || amount === undefined || amount === '') return amount;
  const [int, frac = ''] = String(amount).split('.');
  return `${int}.${frac.replace(/0+$/, '').padEnd(2, '0')}`;
};

export const formatDateTimeUTC = (ts) =>
  ts ? `${new Date(ts).toISOString().slice(0, 16).replace('T', ' ')} UTC` : '—';

export const wageringText = (wagering) =>
  wagering === null || wagering === undefined ? 'not determined — set manually' : `${wagering}x`;

// roundingText describes how the comp amount was rounded and floored.
export const roundingText = (p) =>
  ` (${p.round_to ? `rounded to the nearest ${p.round_to}, ` : ''}minimum ${p.min_amount})`;

// proposalUSD: the converted proposal in USD at today's rate, else its (USD) basis.
export const proposalUSD = (p) => {
  const usd = usdValue(p.issue_amount, p.issue_rate_usd);
  return usd ? formatAmount(usd, 'USD') : formatAmount(p.basis_amount, p.basis_currency);
};

// proposalSummary is the text a manager copies into the back office. overrides: { wagering,
// amount } set by the manager on the page (manual wagering, adjusted amount).
export const proposalSummary = (check, exception = {}, overrides = {}) => {
  const p = check.proposal;
  const usdBasis = p.basis_currency !== p.issue_currency;
  const currency = p.issue_amount ? p.issue_currency : p.basis_currency;
  const proposed = formatAmount(storedAmount(p.issue_amount || p.basis_amount), currency);
  const amount = overrides.amount
    ? `${formatAmount(overrides.amount, currency)} (adjusted from ${proposed})`
    : `${proposed}${usdBasis ? ` (${proposalUSD(p)})` : ''}${p.issue_amount && p.issue_round_to ? `, rounded up to ${p.issue_round_to} ${p.issue_currency}` : ''}`;
  const manualWagering = (p.wagering === null || p.wagering === undefined) && overrides.wagering != null;
  const lines = [
    `${p.name} — player ${check.player.user_id}`,
    `Amount: ${amount}`,
    `Basis: ${p.rate_percent}% of ${formatAmount(check.deposits.measured, check.deposits.basis_currency)} deposits${roundingText(p)}`,
    `Wagering: ${manualWagering ? `${overrides.wagering}x (set manually)` : wageringText(p.wagering)}`,
    `Valid ${p.valid_days} days · activatable until ${formatDateTimeUTC(p.activatable_until)}`,
  ];
  if (rateText(p)) lines.splice(2, 0, `Converted at today's rate: ${rateText(p)}`);
  if (check.decision === 'needs_exception' && exception.granted && exception.reason?.trim()) {
    lines.push(`Exception granted: ${exception.reason.trim()}`);
  }
  return lines.join('\n');
};

// Fiat currencies counted in their own amount when they are the only deposit currency (mirrors
// the backend's NativeFiatCurrencies).
export const NATIVE_FIAT = new Set(['USD', 'EUR', 'AUD', 'CAD', 'NZD']);

// decimalCents parses a 2-decimal amount string into integer cents, so totals stay exact (integers are exact
// in a JS number far beyond any deposit total).
export const decimalCents = (s) => {
  const [int, frac = ''] = String(s).split('.');
  const sign = int.startsWith('-') ? -1 : 1;
  return sign * (Number(int.replace('-', '') || '0') * 100 + Number((frac + '00').slice(0, 2)));
};

export const centsText = (c) => {
  const abs = Math.abs(c);
  return `${c < 0 ? '-' : ''}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
};

// depositTotals: deposits since the start point per currency, plus their USD sum when there is
// more than one currency or a non-fiat one (a single fiat currency is already its own total).
export const depositTotals = (deposits) => {
  const byCurrency = deposits?.by_currency || [];
  const lines = byCurrency.map((d) => ({ currency: d.currency, amount: d.amount }));
  const needsCombined = byCurrency.length > 1 || (byCurrency.length === 1 && !NATIVE_FIAT.has(byCurrency[0].currency));
  const combinedUSD = needsCombined ? centsText(byCurrency.reduce((sum, d) => sum + decimalCents(d.amount_usd), 0)) : null;
  return { lines, combinedUSD };
};
